import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './import-protocols-list.html';
import type { ImportProtocol } from '../services/api.types';
import {
  deleteImportProtocol,
  getImportProtocol,
  listImportProtocols,
} from '../services/api.service';
import { ConfirmDialog } from '../components/confirm-dialog';

@customElement({ name: 'import-protocols-list', template })
export class ImportProtocolsList {
  private readonly i18n: I18N = resolve(I18N);

  public protocols: ImportProtocol[] = [];
  public selectedProtocol: ImportProtocol | null = null;
  public isFetching = false;
  public fetchError = '';

  public async loading(): Promise<void> {
    await this.fetchProtocols();
  }

  public async fetchProtocols(): Promise<void> {
    this.isFetching = true;
    this.fetchError = '';
    try {
      this.protocols = await listImportProtocols();
      if (this.selectedProtocol) {
        this.selectedProtocol = this.protocols.find(
          protocol => protocol.number === this.selectedProtocol?.number,
        ) ?? null;
      }
    } catch (error: unknown) {
      this.fetchError = error instanceof Error
        ? error.message
        : this.i18n.tr('importProtocols.loadFailed');
    } finally {
      this.isFetching = false;
    }
  }

  public async selectProtocol(protocol: ImportProtocol): Promise<void> {
    try {
      this.selectedProtocol = await getImportProtocol(protocol.number);
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('importProtocols.loadFailed'),
        this.i18n.tr('common.error'),
      );
    }
  }

  public openFiles(protocol: ImportProtocol): void {
    window.location.href = `/import-protocol-files?number=${encodeURIComponent(protocol.number)}`;
  }

  public openFilesFromRow(event: Event, protocol: ImportProtocol): void {
    event.stopPropagation();
    this.openFiles(protocol);
  }

  public async removeProtocol(protocol: ImportProtocol): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('importProtocols.deleteConfirm', { number: protocol.number }),
      this.i18n.tr('importProtocols.deleteTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteImportProtocol(protocol.number);
      this.protocols = this.protocols.filter(item => item.number !== protocol.number);
      if (this.selectedProtocol?.number === protocol.number)
        this.selectedProtocol = null;
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('importProtocols.deleteFailed'),
        this.i18n.tr('common.error'),
      );
    }
  }

  public removeProtocolFromRow(event: Event, protocol: ImportProtocol): void {
    event.stopPropagation();
    void this.removeProtocol(protocol);
  }

  public protocolStatus(protocol: ImportProtocol): string {
    return protocol.completedAt
      ? this.i18n.tr('importProtocols.completed')
      : this.i18n.tr('importProtocols.inProgress');
  }

  public outcomeLabel(outcome: string): string {
    const translated = this.i18n.tr(`importProtocols.outcomes.${outcome}`);
    return translated === `importProtocols.outcomes.${outcome}` ? outcome : translated;
  }

  public formatDate(value?: string | null): string {
    return value ? new Date(value).toLocaleString() : '';
  }
}
